import { useCallback, useEffect, useState } from 'react';
import { track } from '../components/analytics';
import { useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { ShareShell } from '../components/share/ShareShell';
import { StatementView } from '../components/share/StatementView';
import {
  clearPassword,
  fetchShare,
  formatDateTime,
  readSavedPassword,
  savePassword,
  shareApiBase,
  unlockShare,
  type ShareDocument,
  type ShareMeta,
} from '../components/share/shareContract';

type State =
  | { status: 'loading' }
  | { status: 'unconfigured' }
  | {
      status: 'password';
      meta: Pick<ShareMeta, 'kind' | 'title'>;
      error?: string;
      attemptsRemaining?: number;
    }
  | { status: 'locked'; lockedUntil?: string }
  | { status: 'not-found' }
  | { status: 'rate-limited' }
  | { status: 'unsupported' }
  | { status: 'error' }
  | { status: 'ready'; document: ShareDocument };

/**
 * A statement someone was sent, at /s/<token>.
 *
 * Cloudflare Pages serves one prerendered shell for every token, so the first render must not depend on
 * the token, on storage or on env — otherwise React hydrates against different markup than the
 * server produced. Everything that varies is read inside the effect, and the first paint is
 * always the skeleton.
 */
export function Share() {
  const { token } = useParams();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      const base = shareApiBase();
      if (!base) {
        setState({ status: 'unconfigured' });
        return;
      }
      if (!token) {
        setState({ status: 'not-found' });
        return;
      }

      // A password already entered in this tab should survive a refresh.
      const saved = readSavedPassword(token);
      if (saved) {
        const unlocked = await unlockShare(base, token, saved, controller.signal);
        if (controller.signal.aborted) return;
        if (unlocked.ok === 'ready') {
          setState({ status: 'ready', document: unlocked.document });
          return;
        }
        clearPassword(token);
        if (unlocked.ok === 'locked') {
          setState({ status: 'locked', lockedUntil: unlocked.lockedUntil });
          return;
        }
        if (unlocked.ok === 'not-found') {
          setState({ status: 'not-found' });
          return;
        }
      }

      const result = await fetchShare(base, token, controller.signal);
      if (controller.signal.aborted) return;

      switch (result.ok) {
        case 'ready':
          setState({ status: 'ready', document: result.document });
          break;
        case 'password':
          setState({ status: 'password', meta: result.meta });
          break;
        case 'not-found':
          setState({ status: 'not-found' });
          break;
        case 'rate-limited':
          setState({ status: 'rate-limited' });
          break;
        case 'locked':
          setState({ status: 'locked', lockedUntil: result.lockedUntil });
          break;
        case 'unsupported':
          setState({ status: 'unsupported' });
          break;
        default:
          setState({ status: 'error' });
      }
    }

    load();
    return () => controller.abort();
  }, [token, attempt]);

  useEffect(() => {
    if (state.status !== 'ready') return;
    // Never the token: this page's own URL is the credential.
    track('share_view', {
      kind: state.document.meta.kind,
      audience: state.document.payload.audience,
    });
  }, [state]);

  const unlock = useCallback(
    async (password: string) => {
      const base = shareApiBase();
      if (!base || !token || state.status !== 'password') return;

      setSubmitting(true);
      const result = await unlockShare(base, token, password);
      setSubmitting(false);

      if (result.ok === 'ready') {
        savePassword(token, password);
        setState({ status: 'ready', document: result.document });
        return;
      }
      if (result.ok === 'locked') {
        setState({ status: 'locked', lockedUntil: result.lockedUntil });
        return;
      }
      if (result.ok === 'not-found') {
        setState({ status: 'not-found' });
        return;
      }
      if (result.ok === 'wrong-password') {
        setState({
          status: 'password',
          meta: state.meta,
          error: 'That password is not right.',
          attemptsRemaining: result.attemptsRemaining,
        });
        return;
      }
      setState({
        status: 'password',
        meta: state.meta,
        error: 'Something went wrong. Try again.',
      });
    },
    [state, token],
  );

  const retry = () => {
    setState({ status: 'loading' });
    setAttempt(value => value + 1);
  };
  const title = state.status === 'ready' ? state.document.payload.title : 'Shared document';

  return (
    <ShareShell>
      <Seo
        description="A statement shared with you through Transport Book."
        noindex
        title={title}
      />
      {state.status === 'loading' && <Skeleton />}
      {state.status === 'ready' && <StatementView document={state.document} />}
      {state.status === 'password' && (
        <PasswordGate
          attemptsRemaining={state.attemptsRemaining}
          error={state.error}
          onSubmit={unlock}
          submitting={submitting}
          title={state.meta.title}
        />
      )}
      {state.status === 'locked' && <Locked lockedUntil={state.lockedUntil} onRetry={retry} />}
      {state.status === 'not-found' && <NotAvailable />}
      {state.status === 'rate-limited' && (
        <Notice
          body="This page has been opened a lot in the last minute. Wait a moment and try again."
          onRetry={retry}
          title="Too many requests"
        />
      )}
      {state.status === 'unsupported' && (
        <Notice
          body="This statement was created by a newer version of Transport Book. Ask the sender to send it again."
          title="This statement cannot be displayed"
        />
      )}
      {state.status === 'error' && (
        <Notice
          body="The statement could not be loaded. Check your connection and try again."
          onRetry={retry}
          title="Something went wrong"
        />
      )}
      {state.status === 'unconfigured' && (
        <Notice
          body="Shared statements are not available on this site yet."
          title="Not available"
        />
      )}
    </ShareShell>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" className="share-doc">
      <h1 className="share-title share-skeleton-line">Loading shared document…</h1>
      <div className="share-skeleton-line" style={{ height: 72 }} />
      <div className="share-skeleton-line" style={{ height: 120 }} />
      <div className="share-skeleton-line" style={{ height: 240 }} />
    </div>
  );
}

function PasswordGate({
  attemptsRemaining,
  error,
  onSubmit,
  submitting,
  title,
}: {
  attemptsRemaining?: number;
  error?: string;
  onSubmit: (password: string) => void;
  submitting: boolean;
  title: string;
}) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);

  return (
    <section className="share-card share-gate">
      <h1>This statement is password protected</h1>
      <p className="share-gate-subject">{title}</p>
      <p>Enter the password the sender gave you. It was sent separately from the link.</p>

      <form
        onSubmit={event => {
          event.preventDefault();
          if (password) onSubmit(password);
        }}
      >
        <div className="field">
          <label htmlFor="share-password">Password</label>
          <div className="share-password-row">
            <input
              autoComplete="current-password"
              id="share-password"
              onChange={event => setPassword(event.target.value)}
              type={show ? 'text' : 'password'}
              value={password}
            />
            <button
              aria-pressed={show}
              className="button secondary"
              onClick={() => setShow(!show)}
              type="button"
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {error && (
          <p className="form-status" data-state="error" role="alert">
            {error}
            {typeof attemptsRemaining === 'number' && attemptsRemaining > 0
              ? ` ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} left before the link locks.`
              : ''}
          </p>
        )}

        <button className="button primary" disabled={submitting || !password} type="submit">
          {submitting ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </section>
  );
}

function Locked({ lockedUntil, onRetry }: { lockedUntil?: string; onRetry: () => void }) {
  return (
    <section className="share-card">
      <h1>Too many attempts</h1>
      <p>
        {lockedUntil
          ? `This link is locked until ${formatDateTime(lockedUntil)}.`
          : 'This link is locked for a while.'}{' '}
        Ask the sender to confirm the password.
      </p>
      <button className="button secondary" onClick={onRetry} type="button">
        Try again
      </button>
    </section>
  );
}

function NotAvailable() {
  return (
    <section className="share-card">
      <h1>This link is not available</h1>
      <p>It may have expired, or the sender may have withdrawn it. Ask them for a new link.</p>
      <a className="button secondary" href="/">
        About Transport Book
      </a>
    </section>
  );
}

function Notice({ body, onRetry, title }: { body: string; onRetry?: () => void; title: string }) {
  return (
    <section className="share-card">
      <h1>{title}</h1>
      <p>{body}</p>
      {onRetry && (
        <button className="button secondary" onClick={onRetry} type="button">
          Try again
        </button>
      )}
    </section>
  );
}
